/* eslint-disable @typescript-eslint/no-explicit-any */
import { useTranslation } from '@/i18n/client';
import to from 'await-to-js';
import cn from 'clsx';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';
// -------------- api -------------------- //
import { cancelAfsRecord, fetchRefundList } from '@/apis/afterSales/afterSales.api';
import type {
  ICancelRecordReq,
  IRefundListReq,
  RefundListResMainVo,
  RefundListResVo,
} from '@/apis/afterSales/afterSales.interface';
import { getOchamaOrderConfig } from '@/apis/order/order.api';
// -------------- 组件 -------------------- //
import CommonButton from '@/app/csr-src/pages/order/components/commonButton';
import Pagination from '@/app/csr-src/pages/order/components/pagination';
import ProductTag from '@/app/csr-src/pages/order/components/productTag';
import UKPageError from '@/components/UKPageError';
import UKPageLoading from '@/components/UKPageLoading';
import UKPageNothing from '@/components/UKPageNothing';
import UKToast from '@/components/UKToast/index';
import CDNImage from '@/components/cdnImage/cdnImage';
import JDIPrice from '@/components/price/index';
import { Dialog, DialogActions, DialogContent, DialogContentText, Tab, Tabs } from '@mui/material';
// -------------- styles -------------------- //
import styles from './returnList.module.scss';
// -------------- 图标 -------------------- //
import VectorDown from '@/assets/icons/VectorDownBig.svg';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
// -------------- utils -------------------- //
// 埋点
import { sendPv } from '@/tracks/25327';
import { EXPOSURE_RECORD, sendClickTrack } from '@/tracks/31835';
// untis

interface ProfileListContext {
  updateNumber: () => void;
  initCartNumHandler: () => void;
}

const ReturnList = () => {
  // 接收父级传入的参数
  const { updateNumber, initCartNumHandler } = useOutletContext<ProfileListContext>();

  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const statusId = queryParams.get('status') || ''; // 获取售后状态

  const { t } = useTranslation();
  const [isNothing, setIsNothing] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [returnListData, setReturnListData] = useState<RefundListResMainVo | null>(null);
  // "serviceStatus": 1, // 售后状态，0-所有售后单，1-正在进行中的售后单，2-已完成的售后单
  const [serviceStatus, setServiceStatus] = useState(Number(statusId) || 0);
  const [page, setPage] = useState(1);
  const [openStates, setOpenStates] = useState<{ [key: string]: boolean }>({});
  const [curReturnItem, setCurReturnItem] = useState<RefundListResVo | null>(null);
  const [afsAgainCancel, setAfsAgainCancel] = useState(false);
  const [ochamaConfig, setOchamaConfig] = useState<{
    refundText: string;
    refundUrl: string;
    refundButtonText: string;
  } | null>(null);

  const returnTabList = [
    {
      name: t('order.orderList.orderTabs.allTab'),
      state: 0,
    },
    {
      name: t('afterSales.returnList.processingTab'),
      state: 1,
    },
  ];

  // 分页change事件
  const pagesChangeHandler = (page: number) => {
    window.scrollTo(0, 0); // 滚动到顶部
    setPage(page);
    getReturnList({ page });
  };

  // 获取列表数据
  const getReturnList = async (params?: { page?: number }) => {
    setIsLoading(true);
    setIsError(false);
    const reqParams = {
      page,
      pageSize: 10,
      serviceStatus: serviceStatus || 0,
      ...params,
    };
    const requestData: IRefundListReq = { ...reqParams };
    const res = await fetchRefundList(requestData);
    if (res?.code === '200' && res?.data) {
      const { data } = res.data;
      if (data && data.length) {
        setReturnListData(res.data);
        setIsNothing(false);
        setIsError(false);
        // setIsLoading(false);
      } else {
        setIsNothing(true);
        setIsError(false);
      }
    } else {
      setIsError(true);
      setIsNothing(false);
    }
    setIsLoading(false);
  };

  // 跳转售后单详情页
  const jumpReturnDetail = (item: RefundListResVo) => {
    // 跳转详情页存储信息
    const scrollY = window.scrollY || document.documentElement.scrollTop;

    const returnInfo = {
      scrollY,
      page: page || 1,
    };
    sessionStorage.setItem('returnInfo', JSON.stringify(returnInfo));

    navigate(`/my-orders/refund?afsServiceId=${item.afsServiceId}&orderId=${item.orderId}`);
  };

  useEffect(() => {
    const getOchamaConfig = async () => {
      const [, configData] = await to(getOchamaOrderConfig());
      setOchamaConfig(configData?.data || null);
    };
    getOchamaConfig();
  }, []);

  useEffect(() => {
    // 售后单列表页埋点
    sendPv.Returns_Service_List();
    const returnInfo = sessionStorage.getItem('returnInfo') || '{}';
    // session 用完后清除，防止其他页面进入后显示不正确
    sessionStorage.removeItem('returnInfo');
    const orderInfoObj = JSON.parse(returnInfo);
    orderInfoObj.page && setPage(orderInfoObj.page);

    getReturnList({
      page: orderInfoObj.page || 1,
    });
  }, [statusId]);

  // 展开与收起功能
  const toggleStatus = (event: React.MouseEvent<HTMLDivElement>, afsServiceId: string) => {
    event.stopPropagation();
    setOpenStates((prevState) => ({
      ...prevState,
      [afsServiceId]: !prevState[afsServiceId],
    }));
  };

  // 取消售后
  const cancelRequestHandler = async () => {
    setAfsAgainCancel(false);
    const requestData: ICancelRecordReq = { afsServiceId: curReturnItem?.afsServiceId || '' };
    const [err, cancelAfsRecordData] = await to(cancelAfsRecord(requestData));

    if (err) {
      UKToast.show({
        content: t('afterSales.statusArea.cancelFailTips'), // 弹窗内容
        icon: 'warning', // 可选：success, fail, loading, warn, info，影响图标
        duration: 2000, // 不写的话默认3000
      });
    } else {
      console.log(cancelAfsRecordData, 'ewe');
      if (parseFloat(cancelAfsRecordData?.code) === 200 && cancelAfsRecordData?.success) {
        UKToast.show({
          content: t('afterSales.statusArea.cancelSuccessTips'), // 弹窗内容
          icon: 'success', // 可选：success, fail, loading, warn, info，影响图标
          duration: 2000, // 不写的话默认3000
        });
        setTimeout(() => {
          updateNumber && updateNumber();
          // getReturnList();
          setPage(1);
          getReturnList({ page: 1 });
        }, 2000);
      } else {
        UKToast.show({
          content: t('afterSales.statusArea.cancelFailTips'), // 弹窗内容
          icon: 'warning', // 可选：success, fail, loading, warn, info，影响图标
          duration: 2000, // 不写的话默认3000
        });
      }
    }
  };

  // tab切换
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setServiceStatus(Number(newValue));
    setPage(1);
    // getReturnList({ page: 1, serviceStatus: newValue });
    const jumpUrl = `/profile/my-returns?status=${newValue}`;
    navigate(jumpUrl, { replace: true });
    // sessionStorage.removeItem("returnInfo");
  };

  // tab内容渲染
  const tabLabelRender = (item: { name: string; state: number }) => {
    return (
      <div className={styles.labelWrap}>
        <div>{item.name}</div>
        {item.state === (serviceStatus || 0) ? <i className={styles.indicatorStyle} /> : null}
      </div>
    );
  };

  return (
    <div className='withList' id='returnList_page_container'>
      <Helmet>
        <title>{t('seo.title.myReturns')} | Joybuy</title>
        <meta name='description' content={t('seo.description.allDesc')} />
      </Helmet>
      {/* 页面标题 */}
      <div className='title_container'>
        <div className='title'>{t('common.pageName.returns')}</div>

        {/* ochama服务单列表入口 */}
        {ochamaConfig?.refundUrl && ochamaConfig?.refundText && ochamaConfig?.refundButtonText && (
          <div className={styles.ochamaListWrap}>
            <div
              exptag={'exp|' + EXPOSURE_RECORD.Ochama_Aftersale_Entry_Exp}
              data-exptag-config='{"useClick": false, "stay_duration": 0.3, "repeated":false, "area_rate": 0.01}'
              className={styles.linkBtnWrap}
              onClick={() => {
                sendClickTrack.Ochama_Aftersale_Entry_Click();
                window.location.href = ochamaConfig?.refundUrl;
              }}
            >
              <div className={styles.ochamaText}>{ochamaConfig.refundText}</div>
              <div className={styles.ochamaButton}>
                {ochamaConfig.refundButtonText}
                <ArrowForwardIosIcon sx={{ fontSize: 14, color: '#E60012', marginLeft: '4px' }} />
              </div>
            </div>
          </div>
        )}
      </div>
      {/* 页面tab */}
      <div className={styles.tabWrap}>
        <Tabs
          value={serviceStatus || 0}
          onChange={handleChange}
          aria-label='order tabs'
          className={styles.returnTabs}
        >
          {returnTabList.map((item: { name: string; state: number }, index: number) => {
            return (
              <Tab
                label={tabLabelRender(item)}
                key={index}
                value={item.state}
                style={{ textTransform: 'none' }}
                disableRipple
              />
            );
          })}
        </Tabs>
      </div>
      {isLoading ? (
        <div className='errorPage'>
          <UKPageLoading />
        </div>
      ) : isNothing ? (
        <div className='errorPage'>
          <UKPageNothing
            style={{ marginTop: '60px' }}
            text={t('order.orderList.emptyPage.returnTip')}
          />
        </div>
      ) : isError ? (
        <div className='errorPage'>
          <UKPageError
            style={{ marginTop: '30px' }}
            text={t('order.orderList.errorPage.tip')}
            btnText={t('order.orderList.errorPage.btnTip')}
            clickTryAgain={() => getReturnList()}
          />
        </div>
      ) : null}
      {!isNothing && !isError && !isLoading && (
        <>
          <div className={styles.wrap}>
            {returnListData?.data && returnListData?.data.length > 0
              ? returnListData?.data.map((item: RefundListResVo, index: number) => {
                  const afsServiceId = item.afsServiceId || '';
                  const showProduct =
                    item?.skuList.length > 3 && !openStates[afsServiceId]
                      ? item?.skuList.slice(0, 3)
                      : item?.skuList;

                  const priceUnit = returnListData?.localInfo?.currencySymbol || '';
                  const price = item?.afsServiceRefundAmount;
                  return (
                    <div
                      className={styles.order_item_wrap}
                      key={index}
                      onClick={() => jumpReturnDetail(item)}
                    >
                      {/* 头部展示 */}
                      <div className={styles.order_item_title}>
                        <div className={cn(styles.left_wrap, styles.center_style)}>
                          <span className={styles.order_status}>
                            {item?.afsServiceStatusTextInOrder}
                          </span>
                        </div>
                        <div className={cn(styles.right_wrap, styles.center_style)}>
                          {price ? (
                            <div className={cn(styles.center_style)}>
                              <span className={styles.title_label_style}>
                                {t('afterSales.returnList.refundTitle')}:
                              </span>
                              <span className={styles.price_style}>{price}</span>
                            </div>
                          ) : null}
                          <div
                            className={cn(
                              styles.center_style,
                              styles.order_info_wrap,
                              styles.to_detail,
                            )}
                          >
                            {t('afterSales.returnList.returnDetails')}
                            <VectorDown className={styles.entry_ico} />
                          </div>
                        </div>
                      </div>
                      {/* 商品图片、价格、数量展示区域 */}
                      {showProduct.map((ele, ind) => {
                        const realPrice = ele.skuSingleShouldPrice || '';
                        const originPrice = ele.skuPrice || '';
                        const showOriginPrice =
                          originPrice && Number(originPrice) > Number(realPrice);
                        return (
                          <div className={styles.product_wrap} key={ind}>
                            <div className={styles.left_product}>
                              <div className={styles.product_info}>
                                <div className={styles.product_img}>
                                  <CDNImage
                                    src={ele.skuImg || ''}
                                    width={60}
                                    height={60}
                                    style={{ objectFit: 'contain', height: '60px' }}
                                  />
                                </div>
                                <div className={styles.product_main_info}>
                                  <div className={styles.product_name}>
                                    <div className={styles.tagsWrap}>
                                      <ProductTag tagList={ele?.showTagList || []} />
                                    </div>
                                    <span className={styles.product_name_cont}>{ele.skuName}</span>
                                  </div>
                                  <div className={styles.product_desc}>{ele.propertyExt}</div>
                                </div>
                              </div>
                            </div>
                            <div className={styles.right_product}>
                              <div className={styles.price_wrap}>
                                {ele?.showFree && ele?.freeText ? (
                                  <div className={styles.freePrice}>{ele.freeText}</div>
                                ) : (
                                  <>
                                    {/* 到手价 */}
                                    <div className={styles.real_price}>
                                      <JDIPrice
                                        symbol={priceUnit}
                                        price={realPrice}
                                        uiType={1}
                                        containerStyle={{
                                          fontSize: '12px',
                                          lineHeight: '18px',
                                        }}
                                        integerStyle={{
                                          fontSize: '18px',
                                          lineHeight: '24px',
                                          height: '24px',
                                        }}
                                      />
                                    </div>
                                    {/* 划线价 */}
                                    {showOriginPrice ? (
                                      <div className={styles.origin_price}>
                                        <JDIPrice
                                          symbol={priceUnit}
                                          price={originPrice}
                                          uiType={2}
                                          containerStyle={{
                                            fontSize: '12px',
                                            lineHeight: '16px',
                                            height: '16px',
                                            display: 'block',
                                          }}
                                          lineThrough
                                        />
                                      </div>
                                    ) : null}
                                  </>
                                )}
                              </div>
                              <div className={styles.product_num}>×{ele.skuCount}</div>
                            </div>
                          </div>
                        );
                      })}
                      {/* 展开与收起功能，只有超过三个才展示 */}
                      {item?.skuList?.length > 3 ? (
                        <div
                          className={styles.view_more}
                          onClick={(e) => toggleStatus(e, afsServiceId)}
                        >
                          {openStates[afsServiceId]
                            ? t('order.orderList.orderItems.retractText')
                            : t('order.orderList.orderItems.viewMoreText')}
                          <VectorDown
                            className={cn(styles.more_ico, {
                              [styles.retract_ico]: openStates[afsServiceId],
                            })}
                          />
                        </div>
                      ) : null}
                      {/* 操作按钮栏展示 */}
                      <div className={styles.operate_wrap}>
                        <div className={styles.btn_group_wrap}>
                          {item?.allowCancel && (
                            <CommonButton
                              variant='outlined'
                              onClick={(e) => {
                                e.stopPropagation();
                                setCurReturnItem(item);
                                setAfsAgainCancel(true);
                              }}
                            >
                              {t('afterSales.afterSalesAccordion.withdraw')}
                            </CommonButton>
                          )}
                          <CommonButton variant='outlined' style={{ marginLeft: '8px' }}>
                            {t('afterSales.returnList.detailsBtn')}
                          </CommonButton>
                        </div>
                      </div>
                    </div>
                  );
                })
              : null}
          </div>
          {returnListData?.total ? (
            <div className='pagination_wrap'>
              <Pagination
                currentPage={page}
                totalPages={Math.ceil(((returnListData?.total || 0) as number) / 10)}
                handlePageChange={pagesChangeHandler}
              />
            </div>
          ) : null}
        </>
      )}
      {/* 取消售后二次确认弹窗 */}
      <Dialog
        open={afsAgainCancel}
        onClose={() => setAfsAgainCancel(false)}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
      >
        <DialogContent>
          <DialogContentText>
            {t('afterSales.statusArea.cancelModal.cancelConfirmText')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <CommonButton variant='outlined' onClick={() => setAfsAgainCancel(false)}>
            {t('afterSales.statusArea.cancelModal.cancelBtnText')}
          </CommonButton>
          <CommonButton variant='contained' onClick={cancelRequestHandler} sx={{ boxShadow: 'none' }}>
            {t('afterSales.statusArea.cancelModal.confirmBtnText')}
          </CommonButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};
export default ReturnList;
